import { QuoteType, RfqBidType, SLQuoteGroupType } from './SE.interfaces';

export const MilestoneCodes = {
  ALL: {
    label: 'All',
    key: 'ALL',
    countLabel: 'TOTAL_ACTIVE_COUNT',
    width: 50,
  },
  DRAFT: {
    label: 'Drafts',
    key: 'DRAFT',
    countLabel: 'DRAFT',
    width: 70,
  },
  AT_ORIGIN: {
    label: 'Origin',
    key: 'AT_ORIGIN',
    countLabel: 'AT_ORIGIN',
    width: 70,
  },
  IN_TRANSIT: {
    label: 'In Transit',
    key: 'IN_TRANSIT',
    countLabel: 'IN_TRANSIT',
    width: 90,
  },
  TRANSSHIPMENT: {
    label: 'Transhipment',
    key: 'TRANSSHIPMENT',
    countLabel: 'TRANSSHIPMENT',
    width: 110,
  },
  DESTINATION: {
    label: 'Destination',
    key: 'DESTINATION',
    width: 100,
    countLabel: 'DESTINATION',
  },
  COMPLETED: {
    label: 'Completed',
    key: 'COMPLETED',
    width: 90,
    countLabel: 'COMPLETED',
  },
  ON_HOLD: {
    label: 'On Hold',
    key: 'ON_HOLD',
    width: 80,
    countLabel: 'ON_HOLD',
  },
  DELETED: {
    label: 'Deleted',
    key: 'DELETED',
    width: 90,
    countLabel: 'DELETED',
  },
};

export const ValidMilestoneCodes = {
  AT_ORIGIN : 'AT_ORIGIN',
  DRAFT : 'DRAFT',
  IN_TRANSIT : 'IN_TRANSIT',
  TRANSSHIPMENT : 'TRANSSHIPMENT',
  DESTINATION : 'DESTINATION',
  COMPLETED : 'COMPLETED',
  ON_HOLD: 'ON_HOLD',
  DELETED: 'DELETED',
};

export const ValidTaskStatusCodes = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  DEADLINE_ELAPSED: 'DEADLINE_ELAPSED',
  DEADLINE_APPROACHING: 'DEADLINE_APPROACHING',
  OPEN: 'OPEN',
};

export const ValidMilestoneAndStatusCodes = {
  ...ValidMilestoneCodes,
  ...ValidTaskStatusCodes,
};

export const SESortOptions = {
  nearest_deadline: 'Nearest Deadline',
  updated_at: 'Last Activity Date',
  created_at: 'Recently Created',
};

export const SEDateFilterOptions = {
  nearestDeadline: 'Nearest Task Deadline',
  updatedAt: 'Last Update',
  createdAt: 'Creation Date',
}

export const RelianceCompanyCodeMapping = [
  { key: 'RJIL', label: 'RJIL' },
  { key: 'RPPMSL', label: 'RPPMSL' },
  { key: 'JPL', label: 'JPL' },
  { key: 'RBL', label: 'RBL' },
];

export enum ProcurementType {
  SPOT = 'SPOT',
  CONTRACT = 'CONTRACT',
}

export const ProcurementTypeLabels: Record<string, string> = {
  [ProcurementType.SPOT]: 'Spot',
  [ProcurementType.CONTRACT]: 'Tender',
};

/**
 * Field-type string for each Quote Details field in the Quotes Selection task (SMT71).
 * Matches the backend FieldValueType.RFQ_QUOTE; routed in SEFieldGroupUtils.
 */
export const RFQ_QUOTE_FIELD_TYPE = 'RFQ_QUOTE';

/** True if a task-details field is a per-vendor Quote Details field (renders as a tab in SMT71). */
export const isRfqQuoteField = (field?: { type?: string }): boolean =>
  field?.type === RFQ_QUOTE_FIELD_TYPE;

/**
 * Field-type string for a per-vendor Quote Details field served as RFQ bids rather than quotes — e.g.
 * Freight Forwarder (SMF431). Routed in SEFieldGroupUtils to `useRfqBids`, which renders the bid
 * selection UI (RfqBidContainerTabs) and fetches from `execution/:id/bids-search`.
 */
export const RFQ_BID_FIELD_TYPE = 'RFQ_BID';

/** True if a task-details field is an RFQ_BID field. */
export const isRfqBidField = (field?: { type?: string }): boolean =>
  field?.type === RFQ_BID_FIELD_TYPE;

/**
 * The Quotes Selection task (SMT71) is identified by the presence of ANY RFQ_QUOTE field in the
 * task-details payload — not by field order, which the backend does not guarantee.
 */
export const isQuoteSelectionTaskFields = (taskFields?: { type?: string }[]): boolean =>
  !!taskFields?.some(isRfqQuoteField);

/**
 * Master codes for the non-quote summary fields on the Quotes Selection task (SMT71). Their values are
 * derived by the frontend (totals computed from selected quotes; reason/remarks captured in the
 * deviation popup) and injected into the submit payload rather than rendered as inline form fields.
 */
export const QUOTE_TOTAL_AMOUNT_CODE = 'SMF423';
export const QUOTE_TOTAL_L1_DEVIATION_CODE = 'SMF424';
export const QUOTE_L1_DEVIATION_REASON_CODE = 'SMF425';
export const QUOTE_DEVIATION_REMARKS_CODE = 'SMF426';

/**
 * Quote Candidates (SMF427) — the full ranked list shown to the ops user at selection time, across
 * ALL included vendor types, stored in a single MULTIPLE RFQ_QUOTE field. The frontend snapshots the
 * displayed quotes here on SMT71 submit so the L1-deviation approver (SMT72) sees the exact list the
 * ops user chose from. Unlike SMF416/417/418 it is NOT a per-vendor selectable tab.
 */
export const QUOTE_CANDIDATES_CODE = 'SMF427';

/**
 * True for the Quote Candidates snapshot field (SMF427) — the full list, not a per-vendor selection.
 * Identified by master code when present; the backend does not always populate the code, so we keep a
 * fallback: a MULTIPLE RFQ_QUOTE field. That fallback USED to be safe because SMF427 was the ONLY
 * multiple RFQ_QUOTE field — every per-vendor Quote Details field was SINGLE. That is no longer true:
 * Shipping Line (SMF430) is now MULTIPLE (one pick per container size, ISS-291047). So the fallback must
 * explicitly EXCLUDE any known per-vendor quote-detail field, otherwise SMF430 would be misread as the
 * candidates snapshot — dropping it from the per-vendor tabs and from the combined totals.
 */
export const isQuoteCandidatesField = (field?: {
  code?: string;
  masterCode?: string;
  type?: string;
  isMultipleEntryType?: boolean;
}): boolean => {
  const code = field?.masterCode || field?.code;
  if (code === QUOTE_CANDIDATES_CODE) return true;
  // A per-vendor quote-detail field (SMF416-421, SMF430) is never the candidates snapshot, even when it
  // is MULTIPLE — so it must not be swept up by the code-less fallback below.
  const isPerVendorDetailCode = !!code && !!QUOTE_DETAILS_MASTER_CODE_TO_VENDOR_TYPE[code];
  return isRfqQuoteField(field) && field?.isMultipleEntryType === true && !isPerVendorDetailCode;
};

/**
 * A per-vendor Quote Details field (SMF416/417/418) — an RFQ_QUOTE field the ops user selects one
 * quote from, rendered as a tab. Excludes the Quote Candidates snapshot (SMF427).
 */
export const isPerVendorQuoteField = (field?: {
  type?: string;
  code?: string;
  masterCode?: string;
  isMultipleEntryType?: boolean;
}): boolean => isRfqQuoteField(field) && !isQuoteCandidatesField(field);

/**
 * A field that renders as a per-vendor tab in the Quote Selection task (SMT71): either an RFQ_QUOTE
 * quote-selection field (rendered by SEQuotePanel) or an RFQ_BID bid-selection field (rendered by
 * RfqBidContainerTabs, e.g. Freight Forwarder SMF431). Excludes the Quote Candidates snapshot
 * (SMF427, always RFQ_QUOTE). The routing to quote-vs-bid UI is by field type in SEFieldGroupUtils —
 * this predicate only decides tab eligibility.
 */
export const isPerVendorQuoteOrBidField = (field?: {
  type?: string;
  code?: string;
  masterCode?: string;
  isMultipleEntryType?: boolean;
}): boolean => isPerVendorQuoteField(field) || (isRfqBidField(field) && !isQuoteCandidatesField(field));

/**
 * Vendor types for quote search. The enum values are the `vendorType` query-param sent to
 * `execution/:id/quote-search` and MUST match the backend ORGANISATION_TYPES enum.
 */
export enum VendorType {
  FF = 'FF',
  SHIPPING_LINE = 'SHIPPING_LINE',
  CHA = 'CHA',
  CFS_CONTRACTOR = 'CFS_CONTRACTOR',
  ICD_CONTRACTOR = 'ICD_CONTRACTOR',
  SURVEYOR = 'SURVEYOR',
  TRANSPORTER = 'TRANSPORTER',
  BREAK_BULK = 'BREAK_BULK',
  INTER_CARTING = 'INTER_CARTING',
}

export const VendorTypeLabels: Record<string, string> = {
  [VendorType.FF]: 'Freight Forwarder',
  [VendorType.SHIPPING_LINE]: 'Shipping Line',
  [VendorType.CHA]: 'CHA',
  [VendorType.CFS_CONTRACTOR]: 'CFS Contractor',
  [VendorType.ICD_CONTRACTOR]: 'ICD Contractor',
  [VendorType.SURVEYOR]: 'Surveyor',
  [VendorType.TRANSPORTER]: 'Transporter',
  [VendorType.BREAK_BULK]: 'Break Bulk',
  [VendorType.INTER_CARTING]: 'Inter Carting',
};

/**
 * Backend field master codes for the Quote Details fields (SMT71), mapped to their vendor type.
 * Master code is the reliable identifier (present on the task-details field payload); the field name
 * is only a fallback.
 */
export const QUOTE_DETAILS_MASTER_CODE_TO_VENDOR_TYPE: Record<string, VendorType> = {
  SMF416: VendorType.CHA,
  SMF417: VendorType.CFS_CONTRACTOR,
  SMF418: VendorType.ICD_CONTRACTOR,
  SMF419: VendorType.SURVEYOR,
  SMF420: VendorType.BREAK_BULK,
  SMF421: VendorType.INTER_CARTING,
  SMF430: VendorType.SHIPPING_LINE,
  SMF431: VendorType.FF,
};

/**
 * Derives the vendor type of a Quote Details field. Prefers the master code, falling back to matching
 * the field name ("Quote Details — CHA / CFS Contractor / Surveyor / Break Bulk ...").
 * `field` is the task-details field payload (extraData) — it carries masterCode/code and name.
 * Shipping Line (SMF430, ISS-291047) and Freight Forwarder (SMF431, ISS-291077) got their master codes
 * later than the rest; the name fallback is kept for shipments whose fields were materialized before
 * those codes existed. It matches the spelled-out vendor name only — see `isFreightForwarderField`.
 * TRANSPORTER (SMF422) is reserved on the backend but never mapped to an SMT71 tab, so it has no entry
 * here.
 */
export const getVendorTypeFromQuoteField = (field?: {
  masterCode?: string;
  code?: string;
  name?: string;
}): VendorType | undefined => {
  const masterCode = field?.masterCode || field?.code;
  if (masterCode && QUOTE_DETAILS_MASTER_CODE_TO_VENDOR_TYPE[masterCode]) {
    return QUOTE_DETAILS_MASTER_CODE_TO_VENDOR_TYPE[masterCode];
  }
  const name = field?.name;
  if (isCfsContractorField(name)) return VendorType.CFS_CONTRACTOR;
  if (isIcdContractorField(name)) return VendorType.ICD_CONTRACTOR;
  if (isChaContractorField(name)) return VendorType.CHA;
  if (isSurveyorField(name)) return VendorType.SURVEYOR;
  if (isBreakBulkVendorField(name)) return VendorType.BREAK_BULK;
  if (isInterCartingContractorField(name)) return VendorType.INTER_CARTING;
  if (isFreightForwarderField(name)) return VendorType.FF;
  if (isShippingLineField(name)) return VendorType.SHIPPING_LINE;
  return undefined;
};

/**
 * Fixed left-to-right order of the per-vendor rate tabs in the Quote Selection task (SMT71, ISS-293412).
 * Tabs render in this sequence regardless of the order the backend returns the Quote Details fields.
 * Only vendor types selected in SMT70 are materialised as fields, so unselected types are simply absent
 * and the remaining tabs collapse up in this order. CFS and ICD are mutually exclusive (enforced by the
 * SMT70 selection validation), so only one of the two ever occupies slot 5. TRANSPORTER is intentionally
 * omitted — it is reserved on the backend and never rendered as a tab; if it (or any future vendor type)
 * ever appears, it falls through to the unmapped case in `getQuoteTabOrder` and sorts last.
 */
export const QUOTE_TAB_VENDOR_ORDER: VendorType[] = [
  VendorType.FF,
  VendorType.SHIPPING_LINE,
  VendorType.BREAK_BULK,
  VendorType.CHA,
  VendorType.CFS_CONTRACTOR,
  VendorType.ICD_CONTRACTOR,
  VendorType.SURVEYOR,
  VendorType.INTER_CARTING,
];

/**
 * Sort rank for a per-vendor quote/bid tab, derived from its field meta (masterCode/code/name). Known
 * vendor types return their index in `QUOTE_TAB_VENDOR_ORDER`; a field whose vendor type is unmapped
 * (a future/legacy vendor type, or Transporter) returns a sentinel so it sorts after all known tabs.
 * Pair with a STABLE sort so unmapped fields keep their original relative order.
 */
export const getQuoteTabOrder = (field?: {
  masterCode?: string;
  code?: string;
  name?: string;
}): number => {
  const vendorType = getVendorTypeFromQuoteField(field);
  const index = vendorType ? QUOTE_TAB_VENDOR_ORDER.indexOf(vendorType) : -1;
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

export interface QuoteTotals {
  totalQuoteAmount: number;
  totalDeviation: number;
}

/**
 * Sums the selected quotes into the SMT71 summary totals. Both are read from the INR-normalised
 * comparable (executionRanking.inr_value) — the backend persists SMF423/SMF424 in INR and the value
 * is currency-agnostic, so quotes in different currencies still total correctly. Shared by the live
 * footer and the submit payload so the displayed and persisted totals cannot drift.
 */
export const getQuoteTotals = (quotes: QuoteType[]): QuoteTotals => {
  const totalQuoteAmount = quotes.reduce(
    (sum, quote) => sum + (quote.executionRanking?.inr_value || 0),
    0,
  );
  const totalDeviation = quotes.reduce((sum, quote) => sum + (Number(quote.deviationFromL1) || 0), 0);
  return { totalQuoteAmount, totalDeviation };
};

/**
 * Maps a selected RFQ bid (RfqBidType) into the QuoteType shape the SMT71 totals consume, so a bid tab
 * (e.g. Freight Forwarder) contributes to the combined Total Quote Amount / Total Deviation exactly
 * like a quote tab. The combined Total Quote Amount sums executionRanking.inr_value, so the bid's
 * INR-normalised ranking charge (chargeForRanking.inr_value from bids-search) is carried straight into
 * inr_value — no currency assumption. `deviationFromL1` feeds the combined Total Deviation Amount.
 */
export const bidToQuote = (bid: RfqBidType, vendorType?: string): QuoteType => ({
  bidId: bid.bidId,
  bidderOrganisationId: bid.bidderOrganisationId,
  bidderOrganisationName: bid.bidderOrganisationName || null,
  vendorType: vendorType || VendorType.FF,
  validityStartDate: bid.validityStartDate,
  validityEndDate: bid.validityEndDate,
  executionRanking: {
    name: bid.chargeForRanking.name,
    amount: bid.chargeForRanking.amount,
    currency: bid.chargeForRanking.currency,
    dollar_value: bid.chargeForRanking.dollar_value,
    exchange_rate: bid.chargeForRanking.exchange_rate,
    inr_value: bid.chargeForRanking.inr_value,
  },
  containerSize: bid.containerSize,
  containerType: bid.containerType,
  rank: bid.rank,
  deviationFromL1: bid.deviationFromL1,
});

/**
 * Flattens an RFQ_BID field value ({ bids: Record<containerSize, RfqBidType[]> }) into the selected
 * QuoteType[] for totalling — one pick per container size, mirroring how Shipping Line contributes its
 * per-size picks. Accepts `unknown` because the SMT71 form holds heterogeneous per-field values
 * (QuoteType for quote tabs, this bid-value object for bid tabs).
 */
export const selectedBidsToQuotes = (fieldValue: unknown, vendorType?: string): QuoteType[] => {
  const bids = (fieldValue as { bids?: Record<string, RfqBidType[] | undefined> } | undefined)?.bids;
  if (!bids) return [];
  return Object.keys(bids).reduce((acc: QuoteType[], category) => {
    const bid = bids[category]?.[0];
    if (bid) acc.push(bidToQuote(bid, vendorType));
    return acc;
  }, [] as QuoteType[]);
};

/**
 * Flattens EVERY fetched bid (all ranks, across all container sizes) from a bids-search result into
 * QuoteType[], for the Quote Candidates snapshot (SMF427) — the full list the ops user chose from,
 * mirroring how each quote panel reports its whole fetched list. Unlike selectedBidsToQuotes, this is
 * not limited to the single pick per size.
 */
export const allBidsToQuotes = (
  bids: Record<string, RfqBidType[] | undefined> | undefined,
  vendorType?: string,
): QuoteType[] => {
  if (!bids) return [];
  return Object.keys(bids).reduce((acc: QuoteType[], category) => {
    (bids[category] || []).forEach((bid) => {
      if (bid) acc.push(bidToQuote(bid, vendorType));
    });
    return acc;
  }, [] as QuoteType[]);
};

/**
 * True when the given picks come from more than one distinct bidder org. Powers the Shipping Line
 * same-vendor rule (ISS-291047): the per-container-size picks must all be from ONE vendor. Enforced as a
 * validation (warning banner + submit gate), not by filtering the lists, so it is shared by the size-tab
 * banner and the task-level submit gate to keep the two in lock-step. Empty/single-pick selections are
 * never a mismatch.
 */
export const quotesSpanMultipleVendors = (quotes: QuoteType[]): boolean =>
  new Set((quotes || []).map((quote) => quote.bidderOrganisationId)).size > 1;

/** Stable identity for a container-size group — `containerSize` + `containerType` (ISS-291047). */
export const getContainerGroupKey = (
  group: Pick<SLQuoteGroupType, 'containerSize' | 'containerType'>,
): string => `${group.containerSize ?? ''}__${group.containerType ?? ''}`;

/**
 * Tab label for a Shipping Line container-size group, e.g. `20 GP (x2)`. Falls back to a generic label
 * when the backend has no container-size data (the single-group flat-list fallback).
 */
export const getContainerGroupLabel = (group: SLQuoteGroupType): string => {
  const sizeType = [group.containerSize, group.containerType].filter(Boolean).join(' ').trim();
  const base = sizeType || 'Quotes';
  return group.containerCount > 0 ? `${base} (x${group.containerCount})` : base;
};

/**
 * Groups a flat quote list by container size + type, preserving encounter order. Used as the fallback
 * when the quote-search response has no `groups` (older backend / SL shipment without size data) so the
 * Shipping Line tab can always render through the same grouped component. Each synthesised group keeps
 * whatever per-quote rank/deviation the backend already assigned.
 */
export const groupQuotesByContainerSize = (quotes: QuoteType[]): SLQuoteGroupType[] => {
  const byKey = new Map<string, SLQuoteGroupType>();
  (quotes || []).forEach((quote) => {
    const key = getContainerGroupKey({
      containerSize: quote.containerSize ?? null,
      containerType: quote.containerType ?? null,
    });
    if (!byKey.has(key)) {
      byKey.set(key, {
        containerSize: quote.containerSize ?? null,
        containerType: quote.containerType ?? null,
        containerCount: 0,
        quotes: [],
      });
    }
    byKey.get(key)!.quotes.push(quote);
  });
  return Array.from(byKey.values());
};

/**
 * SMT70 — Vendor Type Selection.
 * The container-handling sub-fields are identified by their FieldValueType (unique to this task),
 * so the conditional-visibility logic auto-scopes to SMT70 without needing the task master code.
 */
export const CHECKBOX_FIELD_TYPE = 'CHECKBOX';

/**
 * Buffer Days are split into two vendor-specific fields with distinct FieldValueTypes; their option
 * sets (CFS -> 7/14/21, ICD -> 10/21) are served by the generic dropdown endpoint keyed on the type.
 */
export const CFS_BUFFER_DAYS_FIELD_TYPE = 'CFS_BUFFER_DAYS';
export const ICD_BUFFER_DAYS_FIELD_TYPE = 'ICD_BUFFER_DAYS';

/** Sub-fields revealed only when CFS Contractor is selected. */
export const CfsOnlyContainerSubFieldTypes = [
  'SEGMENT',
  'PANEL_IDENTIFIER',
  CFS_BUFFER_DAYS_FIELD_TYPE,
];

/** Sub-fields revealed only when ICD Contractor is selected. */
export const IcdOnlyContainerSubFieldTypes = [
  ICD_BUFFER_DAYS_FIELD_TYPE,
];

/** Sub-fields revealed when either CFS or ICD Contractor is selected (Cargo Type, Gauge Status & Destuff Indicator apply to both). */
export const SharedContainerSubFieldTypes = ['CARGO_TYPE', 'GAUGE_STATUS', 'DESTUFF_INDICATOR'];

/** Sub-fields revealed only when CHA is selected (Material Category, Plant Type). */
export const ChaSubFieldTypes = ['MATERIAL_CATEGORY', 'PLANT_TYPE'];

/**
 * Sub-fields revealed only when Surveyor is selected. Surveyor Type (MWS / Bulk) picks the GPO ranking
 * logic; Surveyor Contract Type (the charge UOM: Per Manday-Per Shift / Per Container / Per MT) completes
 * the GPO match key and selects the rate's quantity multiplier. Both are conditionally required by the
 * backend task-completion handler once Surveyor is ticked.
 */
export const SurveyorSubFieldTypes = ['SURVEYOR_TYPE', 'SURVEYOR_CONTRACT_TYPE'];

/** Sub-fields revealed only when Inter-Carting is selected (Contract Type). */
export const InterCartingSubFieldTypes = ['INTER_CARTING_CONTRACT_TYPE'];

/** Sub-fields revealed only when Freight Forwarder is selected (FF Type). */
export const FFSubFieldTypes = ['FF_TYPE'];

/** FF Type option key that, when selected, disables the SL (Shipping Line) vendor checkbox. */
export const FF_TYPE_FF_DIRECT_CONTRACT = 'FF_DIRECT_CONTRACT';

export const AllContainerSubFieldTypes = [
  ...CfsOnlyContainerSubFieldTypes,
  ...IcdOnlyContainerSubFieldTypes,
  ...SharedContainerSubFieldTypes,
];

/**
 * The vendor checkboxes share FieldValueType `CHECKBOX`, and the task-details field response carries
 * no master code — so they are matched by name (which contains "CFS" / "ICD" / "CHA" / "Surveyor").
 */
export const isCfsContractorField = (name?: string): boolean => /\bcfs\b/i.test(name || '');
export const isIcdContractorField = (name?: string): boolean => /\bicd\b/i.test(name || '');
export const isChaContractorField = (name?: string): boolean => /\bcha\b/i.test(name || '');
export const isSurveyorField = (name?: string): boolean => /surveyor/i.test(name || '');
export const isInterCartingContractorField = (name?: string): boolean => /inter\s*carting/i.test(name || '');
/**
 * Deliberately does NOT match the bare abbreviation "FF". The Quote Details label spells the name out
 * ("Quote Details — Freight Forwarder"), so the long form is all this needs; the only thing named a bare
 * "FF" is the SMT70 INCLUDE_FF *checkbox*. Matching that would silently change an unrelated flow —
 * `isHiddenVendorField` (SEFieldGroup) uses this predicate to hide the FF/SL vendor checkboxes on
 * break-bulk shipments — and would do it asymmetrically, since INCLUDE_SHIPPING_LINE is named "SL" and
 * `isShippingLineField` does not match that either. Hiding both is a separate fix.
 */
export const isFreightForwarderField = (name?: string): boolean => /freight\s*forwarder/i.test(name || '');
/**
 * The SMT70 Vendor-Type Selection checkbox for Freight Forwarder is named the bare abbreviation "FF"
 * (task-details code SMF397) — NOT the spelled-out "Freight Forwarder". This predicate is used ONLY to
 * reveal the FF Type sub-field (FF_TYPE) when that checkbox is ticked; it is intentionally kept separate
 * from `isFreightForwarderField` so the break-bulk checkbox-hiding flow (which matches the spelled-out
 * name) is left untouched and no FF/SL asymmetry is introduced. Exact-match so it can't collide with
 * unrelated names that merely contain "ff"; the spelled-out form is also accepted for configs that use it.
 */
export const isFFVendorCheckboxField = (name?: string): boolean =>
  /^\s*ff\s*$/i.test(name || '') || isFreightForwarderField(name);
export const isShippingLineField = (name?: string): boolean => /shipping\s*line/i.test(name || '');
/**
 * The SMT70 Vendor-Type Selection checkbox for Shipping Line is named the bare abbreviation "SL"
 * (task-details code SMF398) — NOT the spelled-out "Shipping Line". Kept separate from
 * `isShippingLineField` (which the break-bulk hiding flow relies on) for the same reasons as
 * [[isFFVendorCheckboxField]]. Exact-match so it can't collide with names merely containing "sl".
 */
export const isSLVendorCheckboxField = (name?: string): boolean =>
  /^\s*sl\s*$/i.test(name || '') || isShippingLineField(name);
export const isBreakBulkVendorField = (name?: string): boolean => /break\s*bulk/i.test(name || '');

/**
 * Mode-driven vendor-type visibility for SMT70:
 *  - Break Bulk mode  -> hide Freight Forwarder + Shipping Line, show Break Bulk Vendor
 *  - FCL / LCL / Air  -> show Freight Forwarder + Shipping Line, hide Break Bulk Vendor
 */
export const isBreakBulkMode = (mode?: string): boolean =>
  (mode || '').toUpperCase() === 'BREAKBULK';

export const myTaskListStatus = [
  'Done',
  'In Progress',
  'Cancelled',
  'Not Started',
  'Sent For Approval',
  'Rejected',
  'Amend Task',
];

export const taskResourceTypes = {
  DOCUMENTS: 'DOCUMENTS',
  CONTAINERS: 'CONTAINERS',
  AWBS: 'AWBS'
}

export const sortOrderTypes ={
  ASCENDING :'ASC',
  DESCENDING :'DESC'
}
export const seachFilterOptions = [
  { key: 'ALL', label: 'All' },
  { key: 'SHIPMENT #', label: 'Shipment Number' },
  { key: 'BOOKING #', label: 'Booking Number' },
  { key: 'CLIENT REF #', label: 'Client Reference Number' },
  { key: 'SALES ORDER #', label: 'Sales Order Number' },
  { key: 'PURCHASE ORDER #', label: 'Purchase Order Number'},
  { key: 'INVOICE #', label: 'Invoice Number'},
  { key: 'CUSTOM FIELDS', label: 'Custom Fields'},
];

export const bulkUploadTabs = {
  'PIC_ASSIGNMENT': 'PIC Assignment',
  'CONTAINER_FIELDS': 'Container Fields',
}

export const bulkUpdatesearchFilterOptions = [
  { key: 'CLIENT REF #', label: 'Internal Reference Number' },
  { key: 'SHIPMENT #', label: 'Shipsy Reference Number' },
  { key: 'SALES ORDER #', label: 'SO Number' },
  { key: 'PURCHASE ORDER #', label: 'PO Number'},
]

export const MAX_PIC_ASSIGNMENTS = 10;